import {
  AuthService,
  LoggerService,
  coreServices,
  createBackendPlugin,
} from '@backstage/backend-plugin-api';
import { createRouter } from './service/router';
import { PagerDutyBackendDatabase, PagerDutyBackendStore } from './db';
import {
  readSyncLogsCleanupConfig,
  runSyncLogsCleanup,
  SYNC_LOGS_CLEANUP_INITIAL_DELAY_MINUTES,
  SYNC_LOGS_CLEANUP_TASK_ID,
  SYNC_LOGS_CLEANUP_TIMEOUT_MINUTES,
} from './services/syncLogsCleanup';
import { CatalogClient } from '@backstage/catalog-client';

class CatalogFetchApi {
  constructor(
    private readonly logger: LoggerService,
    private readonly auth: AuthService,
  ) { }

  async fetch(
    input: unknown,
    init: RequestInit | undefined,
  ): Promise<Response> {
    const request = new Request(input as RequestInfo | URL, init);
    const { token } = await this.auth.getPluginRequestToken({
      onBehalfOf: await this.auth.getOwnServiceCredentials(),
      targetPluginId: 'catalog',
    });

    request.headers.set('Authorization', `Bearer ${token}`);
    this.logger.debug(`Added token to outgoing request to ${request.url}`);
    return fetch(request);
  }
}

/** @public */
export const pagerDutyPlugin = createBackendPlugin({
  pluginId: 'pagerduty',
  register(env) {
    env.registerInit({
      deps: {
        logger: coreServices.logger,
        config: coreServices.rootConfig,
        httpRouter: coreServices.httpRouter,
        database: coreServices.database,
        discovery: coreServices.discovery,
        auth: coreServices.auth,
        cache: coreServices.cache,
        scheduler: coreServices.scheduler,
      },
      async init({
        config,
        logger,
        httpRouter,
        database,
        discovery,
        auth,
        cache,
        scheduler,
      }) {
        const pagerDutyBackendStore: PagerDutyBackendStore =
          await PagerDutyBackendDatabase.create(await database.getClient(), {
            skipMigrations: false,
          });

        const cleanupConfig = readSyncLogsCleanupConfig(config);
        if (cleanupConfig.enabled) {
          await scheduler.scheduleTask({
            id: SYNC_LOGS_CLEANUP_TASK_ID,
            frequency: { minutes: cleanupConfig.frequencyMinutes },
            timeout: { minutes: SYNC_LOGS_CLEANUP_TIMEOUT_MINUTES },
            initialDelay: { minutes: SYNC_LOGS_CLEANUP_INITIAL_DELAY_MINUTES },
            scope: 'global',
            fn: () =>
              runSyncLogsCleanup({
                store: pagerDutyBackendStore,
                logger,
                cleanupConfig,
              }),
          });
        }

        httpRouter.use(
          await createRouter({
            config,
            logger,
            store: pagerDutyBackendStore,
            discovery,
            auth,
            cache,
            catalogApi: new CatalogClient({
              discoveryApi: discovery,
              fetchApi: new CatalogFetchApi(logger, auth),
            }),
          }),
        );
        // The default Backstage behaviour is to require authentication on routes.
        // https://backstage.io/docs/backend-system/core-services/http-router/#using-the-service
        // Setting enableUnauthenticatedAccess to true will allow unauthenticated access to the PagerDuty plugin routes.
        const enableUnauthenticatedAccess: boolean = config.getOptionalBoolean('pagerDuty.enableUnauthenticatedAccess') ?? false;
        if (enableUnauthenticatedAccess === true) {
          httpRouter.addAuthPolicy({
            path: '/',
            allow: 'unauthenticated',
          });
        }
      },
    });
  },
});
